/**
 * Brochure-owned marketing copy that has no home in `/tiers.json`.
 *
 * Found during story 155's review (of story 173, the document's producer):
 * the stored tier item carries no marketing-feature attribute at all — only
 * `feature_limits_json` (numeric entitlement limits) and AppConfig-resolved
 * `capabilities`, neither of which is a bullet list. `features` in the
 * document is therefore always `[]`, and will stay that way until a future
 * story adds a stored attribute, an admin-portal editor to maintain it, and
 * a contract change — none of which this story invents.
 *
 * DECISION (story 155): keep the bullets as brochure-owned copy rather than
 * rendering no bullets at all. Losing them entirely would be a visible
 * content regression on a live marketing page for zero gain (the pipeline
 * that story fixed is name/display_name/price drift, not feature-bullet
 * drift, which doesn't exist anywhere yet). See
 * `duvi-niche-site-pricing.test.tsx`'s "does not lose feature bullets" test,
 * which fails if this module is bypassed.
 *
 * Keyed by `name` (the tiers.json match key, immutable) rather than
 * `display_name` (mutable, admin-edited) precisely because a display-name
 * rename must not also blank out the bullets. A tier `name` with no entry
 * here (a brand-new admin-created tier this copy hasn't been written for
 * yet) renders with only the no-payment line rather than guessing or
 * crashing.
 *
 * The wording is the niche's own: Fishwalkers walks people's pet fish, so the
 * unit a walker sells is a walk and the area they cover is a round. The
 * design system's voice notes name "anglers", "guides", "trips" and
 * "charters" as words that must never appear (story 782).
 */
const TIER_FEATURE_COPY: Record<string, string[]> = {
  starter: ["Up to 3 walk types", "Standard search placement"],
  standard: [
    "Unlimited walk types",
    "More than one round",
    "Priority search placement",
  ],
  pro: [
    "Everything in Standard",
    "Custom branding colours",
    "Featured placement",
  ],
};

/**
 * No payment gate exists yet for any tier (subscription billing is a later
 * epic; `price_pence` in the document today is a catalogue price, not
 * something the sign-up flow charges) — one line, appended to every tier's
 * bullets exactly as the brochure prototype's pricing cards carry it.
 */
export const DUVI_NICHE_SITE_TIER_NO_PAYMENT_LINE =
  "No payment required to start";

/**
 * What the price slot says. Deliberately not `price_pence`: nothing charges
 * it, so rendering a £ amount beside a sign-up CTA would state a commitment
 * the flow does not make. The prototype's own pricing cards read the same
 * way, which is why "Free"/"to start" is the honest rendering today rather
 * than a placeholder to be swapped for a number.
 */
export const DUVI_NICHE_SITE_TIER_PRICE = "Free";
export const DUVI_NICHE_SITE_TIER_PRICE_PERIOD = "to start";

/**
 * The bullets for one tier: its brochure-owned copy (if any), always closing
 * on the no-payment line.
 */
export function duviNicheSiteTierFeatures(name: string): string[] {
  return [
    ...(TIER_FEATURE_COPY[name.toLowerCase()] ?? []),
    DUVI_NICHE_SITE_TIER_NO_PAYMENT_LINE,
  ];
}
